import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import React, { CSSProperties, ReactElement, useContext, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { WarningOutlined } from '@mui/icons-material';
import { Chip, Divider, Skeleton } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { formatValue, percentageFormat } from '../../../../../helpers/helpers';
import { UserContext } from '../../../../../context/UserContext';
import { NO_VALUE_MARK } from '../../../dashboardHelpers';
import { formatPeriodLabel } from '../../../../../helpers/dateUtils';


export interface NumericChartComparedProps {
    title: string;
    subtitle: string;
    currency?: string;
    data?: number;
    dataCompared?: number;
    percent?: number;
    percentCompared?: number;
    buttonText?: string;
    isLoading?: boolean;
    isError?: boolean;
    linkChart?: string;
    currentDateFrom: string;
    currentDateTo: string;
    comparedDateFrom: string;
    comparedDateTo: string;
}

const DataFragment = ({
    range,
    mainData,
    percentData,
    chipColor,
    bgChipColor,
    percentIcon
}: {
    range?: string,
    mainData?: number | string,
    percentData?: number | string,
    chipColor: CSSProperties,
    bgChipColor: CSSProperties,
    percentIcon?: ReactElement
}) =>
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginBottom: '16px' }}>
        {range && <Chip label={range} 
         size='small'
        sx={{
            bgcolor: bgChipColor,
            margin: '10px 0px',
            '& .MuiChip-label': {
                color: chipColor
            }
           
        }} />}
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
            }}
        >
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <Typography>{mainData}</Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                {percentIcon}<Typography>{percentData}</Typography>
            </Box>
        </Box>
    </Box>

const NumericChartCompared = ({
    title,
    subtitle,
    currency,
    data,
    dataCompared,
    percent,
    percentCompared,
    buttonText,
    isLoading,
    isError,
    linkChart,
    currentDateFrom,
    currentDateTo,
    comparedDateFrom,
    comparedDateTo,
}: NumericChartComparedProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const dataFormatted = useMemo(() => {
        if (data === undefined || Number.isNaN(data)) return NO_VALUE_MARK
        return data ? formatValue(data, Boolean(currency), currency, locale, 2, false, true) : data
    }, [data]);

    const dataComparedFormatted = useMemo(() => {
        if (Number.isNaN(dataCompared)) return NO_VALUE_MARK
        return data ? formatValue(dataCompared, Boolean(currency), currency, locale, 2, false, true) : dataCompared
    }, [dataCompared]);

    const percentFormatted = useMemo(() => {
        if (!percent) return NO_VALUE_MARK;
        return percentageFormat(percent, locale, 2)
    }, [percent]);

    const percentComparedFormatted = useMemo(() => {
        if (!percentCompared) return NO_VALUE_MARK;
        return percentageFormat(percentCompared, locale, 2)
    }, [percentCompared]);

    const percentIcon = useMemo(() => {
        if (!!percent && percent < 0) return <TrendingDownIcon sx={{ marginRight: '8px', color: theme.palette.error.main }} />
        if (!!percent && percent > 0) return <TrendingUpIcon sx={{ marginRight: '8px', color: theme.palette.success.dark }} />
        return undefined
    }, [percent])

    const percentComparedIcon = useMemo(() => {
        if (!!percentCompared && percentCompared < 0) return <TrendingDownIcon sx={{ marginRight: '8px', color: theme.palette.error.main }} />
        if (!!percentCompared && percentCompared > 0) return <TrendingUpIcon sx={{ marginRight: '8px', color: theme.palette.success.dark }} />
        return undefined
    }, [percentCompared])

    const currentDateLabel = useMemo(() => `${formatPeriodLabel(
        new Date(currentDateFrom),
        locale
      )} - ${formatPeriodLabel(new Date(currentDateTo), locale)}`, [currentDateFrom, currentDateTo]);
    
      const comparedDateLabel = useMemo(() => `${formatPeriodLabel(
        new Date(comparedDateFrom),
        locale
      )} - ${formatPeriodLabel(new Date(comparedDateTo), locale)}`, [comparedDateFrom, comparedDateTo]);

    return (
        <>
            {!isLoading && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1
                }}>
                    <Card elevation={0} sx={{
                        border: `solid 1px`,
                        borderColor: theme.palette.grey['12p'],
                        marginX: '8px',
                        padding: '12px',
                        flex: 1,
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: 'auto', alignItems: 'center', textAlign: 'center', margin: '6px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
                                <Typography variant="eventTitle">{title}</Typography>
                                {dataFormatted === NO_VALUE_MARK && <Chip icon={<WarningOutlined />} label={t("screens.dashboarding.dataUnavailable")}
                                 size='small'
                                sx={{
                                    bgcolor: theme?.palette?.warning['190p'],
                                    margin: '10px 0px',
                                    '& .MuiChip-label': {
                                        color: theme.palette.warning['160p']
                                    }
                                }} />}
                                {dataFormatted !== NO_VALUE_MARK &&
                                    <Typography variant="body2" sx={{ marginTop: '5px' }}>
                                        {subtitle}
                                    </Typography>
                                }
                            </Box>
                        </Box>

                        <DataFragment
                            mainData={dataFormatted}
                            range={currentDateLabel}
                            percentData={percentFormatted}
                            chipColor={theme.palette.info['160p'] as CSSProperties}
                            bgChipColor={theme.palette.info['190p'] as CSSProperties}
                            percentIcon={percentIcon}
                        />
                        <Divider />
                        <DataFragment
                            mainData={dataComparedFormatted}
                            range={comparedDateLabel}
                            percentData={percentComparedFormatted}
                            chipColor={'theme.palette.amber.light' as CSSProperties}
                            bgChipColor={theme.palette.warning['190p'] as CSSProperties}
                            percentIcon={percentComparedIcon}
                        />
                        {buttonText &&
                            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
                                <Button href={linkChart} sx={{ fontSize: '16px', flex: 1, fontWeight: '600', color: 'primary.main' }}>
                                    {buttonText}
                                </Button>
                            </Box>
                        }
                    </Card>
                </Box>
            )}
            {isLoading &&
                <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: '16px' }}>
                    <Card elevation={0} sx={{ border: 'solid 1px', borderColor: theme.palette.grey['12p'], marginX: '8px', padding: '12px', flex: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent: 'space-between', margin: '6px' }}>
                            <Box>
                                <Skeleton variant="text" width="80%" height={18} sx={{ marginBottom: '10px' }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                                <Skeleton variant="text" width={50} height={34} sx={{ marginRight: '8px' }} />
                                <Skeleton variant="text" width={60} height={34} />
                            </Box>
                        </Box>
                    </Card>
                </Box>
            }
        </>
    )
}

export default NumericChartCompared;
